import { Mark, Node } from "@tiptap/pm/model";
import { Editor } from "@tiptap/react";
import markdownIt, { Token } from "markdown-it";
import { MarkdownParser, MarkdownSerializer } from "prosemirror-markdown";
import { Fragment, MarkSpec, Schema } from "prosemirror-model";

// Modified from https://github.com/ProseMirror/prosemirror-markdown

const schema = new Schema({
  nodes: {
    doc: {
      content: "block+",
    },
    paragraph: {
      content: "inline*",
      group: "block",
      parseDOM: [{ preserveWhitespace: "full", tag: "p" }],
      toDOM() {
        return ["p", 0];
      },
    },
    blockquote: {
      content: "block+",
      group: "block",
      parseDOM: [{ tag: "blockquote" }],
      toDOM() {
        return ["blockquote", 0];
      },
    },
    horizontalRule: {
      group: "block",
      parseDOM: [{ tag: "hr" }],
      toDOM() {
        return ["div", ["hr"]];
      },
    },
    heading: {
      attrs: { level: { default: 1 } },
      content: "(text | image)*",
      group: "block",
      defining: true,
      parseDOM: [
        { tag: "h1", attrs: { level: 1 } },
        { tag: "h2", attrs: { level: 2 } },
        { tag: "h3", attrs: { level: 3 } },
        { tag: "h4", attrs: { level: 4 } },
        { tag: "h5", attrs: { level: 5 } },
        { tag: "h6", attrs: { level: 6 } },
      ],
      toDOM(node) {
        return ["h" + node.attrs.level, 0];
      },
    },
    codeBlock: {
      content: "text*",
      group: "block",
      code: true,
      defining: true,
      marks: "",
      attrs: { params: { default: "" } },
      parseDOM: [
        {
          tag: "pre",
          preserveWhitespace: "full",
          getAttrs: (node) => ({ params: (node as HTMLElement).getAttribute("data-params") || "" }),
        },
      ],
      toDOM(node) {
        return ["pre", node.attrs.params ? { "data-params": node.attrs.params } : {}, ["code", 0]];
      },
    },
    orderedList: {
      content: "listItem+",
      group: "block",
      attrs: { order: { default: 1 }, tight: { default: false } },
      parseDOM: [
        {
          tag: "ol",
          getAttrs(dom) {
            return {
              order: (dom as HTMLElement).hasAttribute("start") ? +(dom as HTMLElement).getAttribute("start")! : 1,
              tight: (dom as HTMLElement).hasAttribute("data-tight"),
            };
          },
        },
      ],
      toDOM(node) {
        return [
          "ol",
          { "start": node.attrs.order == 1 ? null : node.attrs.order, "data-tight": node.attrs.tight ? "true" : null },
          0,
        ];
      },
    },
    bulletList: {
      content: "listItem+",
      group: "block",
      attrs: { tight: { default: false } },
      parseDOM: [{ tag: "ul", getAttrs: (dom) => ({ tight: (dom as HTMLElement).hasAttribute("data-tight") }) }],
      toDOM(node) {
        return ["ul", { "data-tight": node.attrs.tight ? "true" : null }, 0];
      },
    },
    listItem: {
      content: "block+",
      defining: true,
      parseDOM: [{ tag: "li" }],
      toDOM() {
        return ["li", 0];
      },
    },
    text: {
      group: "inline",
    },
    image: {
      inline: true,
      attrs: {
        src: {},
        alt: { default: null },
        title: { default: null },
      },
      group: "inline",
      draggable: true,
      parseDOM: [
        {
          tag: "img[src]",
          getAttrs(dom) {
            return {
              src: (dom as HTMLElement).getAttribute("src"),
              title: (dom as HTMLElement).getAttribute("title"),
              alt: (dom as HTMLElement).getAttribute("alt"),
            };
          },
        },
      ],
      toDOM(node) {
        return ["img", node.attrs];
      },
    },
    hardBreak: {
      inline: true,
      group: "inline",
      selectable: false,
      parseDOM: [{ tag: "br" }],
      toDOM() {
        return ["br"];
      },
    },
  },
  marks: {
    italic: {
      parseDOM: [
        { tag: "i" },
        { tag: "em" },
        { style: "font-style=italic" },
        { style: "font-style=normal", clearMark: (m) => m.type.name == "em" },
      ],
      toDOM() {
        return ["em"];
      },
    },
    bold: {
      parseDOM: [
        { tag: "strong" },
        { tag: "b", getAttrs: (node) => node.style.fontWeight != "normal" && null },
        { style: "font-weight=400", clearMark: (m) => m.type.name == "strong" },
        { style: "font-weight", getAttrs: (value) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null },
      ],
      toDOM() {
        return ["strong"];
      },
    } as MarkSpec,
    strike: {
      parseDOM: [
        { tag: "s" },
        { tag: "del" },
        { tag: "strike" },
        { style: "text-decoration=line-through" },
        { style: "text-decoration", getAttrs: (value) => value == "line-through" && null },
      ],
      toDOM() {
        return ["s"];
      },
    },
    link: {
      attrs: {
        href: {},
        title: { default: null },
      },
      inclusive: false,
      parseDOM: [
        {
          tag: "a[href]",
          getAttrs(dom) {
            return { href: (dom as HTMLElement).getAttribute("href"), title: dom.getAttribute("title") };
          },
        },
      ],
      toDOM(node) {
        return ["a", node.attrs];
      },
    },
    code: {
      code: true,
      parseDOM: [{ tag: "code" }],
      toDOM() {
        return ["code"];
      },
    },
  },
});

const defaultMarkdownSerializer = new MarkdownSerializer(
  {
    blockquote(state, node) {
      state.wrapBlock("> ", null, node, () => state.renderContent(node));
    },
    codeBlock(state, node) {
      // Make sure the front matter fences are longer than any dash sequence within it
      const backticks = node.textContent.match(/`{3,}/gm);
      const fence = backticks ? backticks.sort().slice(-1)[0] + "`" : "```";

      state.write(fence + (node.attrs.params || "") + "\n");
      state.text(node.textContent, false);
      // Add a newline to the current content before adding closing marker
      state.write("\n");
      state.write(fence);
      state.closeBlock(node);
    },
    heading(state, node) {
      state.write(state.repeat("#", node.attrs.level) + " ");
      state.renderInline(node, false);
      state.closeBlock(node);
    },
    horizontalRule(state, node) {
      state.write(node.attrs.markup || "---");
      state.closeBlock(node);
    },
    bulletList(state, node) {
      state.renderList(node, "  ", () => (node.attrs.bullet || "*") + " ");
    },
    orderedList(state, node) {
      const start = node.attrs.order || 1;
      const maxW = String(start + node.childCount - 1).length;
      const space = state.repeat(" ", maxW + 2);
      state.renderList(node, space, (i) => {
        const nStr = String(start + i);
        return state.repeat(" ", maxW - nStr.length) + nStr + ". ";
      });
    },
    listItem(state, node) {
      state.renderContent(node);
    },
    paragraph(state, node) {
      state.renderInline(node);
      state.closeBlock(node);
    },
    image(state, node) {
      state.write(
        "![" +
          state.esc(node.attrs.alt || "") +
          "](" +
          node.attrs.src.replace(/[\(\)]/g, "\\$&") +
          (node.attrs.title ? ' "' + node.attrs.title.replace(/"/g, '\\"') + '"' : "") +
          ")\n",
      );
    },
    video(state, node) {
      state.write("![](" + node.attrs.src.replace(/[\(\)]/g, "\\$&") + ")\n");
    },
    hardBreak(state, node, parent, index) {
      for (let i = index + 1; i < parent.childCount; i++)
        if (parent.child(i).type != node.type) {
          state.write("\\\n");
          return;
        }
    },
    text(state, node) {
      state.text(node.text!, !(state as any).inAutolink);
    },
  },
  {
    italic: { open: "*", close: "*", mixable: true, expelEnclosingWhitespace: true },
    bold: { open: "**", close: "**", mixable: true, expelEnclosingWhitespace: true },
    strike: { open: "~~", close: "~~", mixable: true, expelEnclosingWhitespace: true },
    link: {
      open(state, mark, parent, index) {
        (state as any).inAutolink = isPlainURL(mark, parent, index);
        return (state as any).inAutolink ? "<" : "[";
      },
      close(state, mark) {
        const { inAutolink } = state as any;
        (state as any).inAutolink = undefined;
        return inAutolink
          ? ">"
          : "](" +
              mark.attrs.href.replace(/[\(\)"]/g, "\\$&") +
              (mark.attrs.title ? ` "${mark.attrs.title.replace(/"/g, '\\"')}"` : "") +
              ")";
      },
      mixable: true,
    },
    code: {
      open(_state, _mark, parent, index) {
        return backticksFor(parent.child(index), -1);
      },
      close(_state, _mark, parent, index) {
        return backticksFor(parent.child(index - 1), 1);
      },
      escape: false,
    },
  },
);

export const defaultMarkdownParser = new MarkdownParser(
  schema,
  markdownIt("default", { html: false }).disable(["table"]),
  {
    blockquote: { block: "blockquote" },
    paragraph: { block: "paragraph" },
    list_item: { block: "listItem" },
    bullet_list: { block: "bulletList", getAttrs: (_, tokens, i) => ({ tight: listIsTight(tokens, i) }) },
    ordered_list: {
      block: "orderedList",
      getAttrs: (tok, tokens, i) => ({
        order: +tok.attrGet("start")! || 1,
        tight: listIsTight(tokens, i),
      }),
    },
    heading: { block: "heading", getAttrs: (tok) => ({ level: +tok.tag.slice(1) }) },
    code_block: { block: "codeBlock", noCloseToken: true },
    fence: { block: "codeBlock", getAttrs: (tok) => ({ params: tok.info || "" }), noCloseToken: true },
    hr: { node: "horizontalRule" },
    image: {
      node: "image",
      getAttrs: (tok) => ({
        src: tok.attrGet("src"),
        title: tok.attrGet("title") || null,
        alt: (tok.children![0] && tok.children![0].content) || null,
      }),
    },
    hardbreak: { node: "hardBreak" },
    em: { mark: "italic" },
    strong: { mark: "bold" },
    s: { mark: "strike" },
    link: {
      mark: "link",
      getAttrs: (tok) => ({
        href: tok.attrGet("href"),
        title: tok.attrGet("title") || null,
      }),
    },
    code_inline: { mark: "code", noCloseToken: true },
  },
);

function listIsTight(tokens: readonly Token[], i: number) {
  while (++i < tokens.length) if (tokens[i].type != "list_item_open") return tokens[i].hidden;
  return false;
}

function backticksFor(node: Node, side: number) {
  const ticks = /`+/g;
  let m,
    len = 0;
  if (node.isText) while ((m = ticks.exec(node.text!))) len = Math.max(len, m[0].length);
  let result = len > 0 && side > 0 ? " `" : "`";
  for (let i = 0; i < len; i++) result += "`";
  if (len > 0 && side < 0) result += " ";
  return result;
}

function isPlainURL(link: Mark, parent: Node, index: number) {
  if (link.attrs.title || !/^\w+:/.test(link.attrs.href)) return false;
  const content = parent.child(index);
  if (!content.isText || content.text != link.attrs.href || content.marks[content.marks.length - 1] != link)
    return false;
  return index == parent.childCount - 1 || !link.isInSet(parent.child(index + 1).marks);
}

// Exports

export function tiptapEditorToMarkdown(editor: Editor) {
  return defaultMarkdownSerializer.serialize(Node.fromJSON(schema, editor.getJSON()));
}

export function richTextEditorDocumentJSONStringToMarkdown(json: string) {
  if (!json) return "";
  return defaultMarkdownSerializer.serialize(Node.fromJSON(schema, JSON.parse(json)));
}

export function markdownToRichTextEditorDocumentJSON(markdown: string) {
  const doc = defaultMarkdownParser.parse(markdown);
  const cleanedDoc = removeEmptyParagraphsAroundImages(doc);
  return cleanedDoc.toJSON();
}

export function richTextEditorDocumentJSONStringToProseMirrorNode(json: string) {
  return Node.fromJSON(schema, JSON.parse(json));
}

export function richTextEditorEmptyDocumentJSONString() {
  return JSON.stringify({
    type: "doc",
    content: [
      {
        type: "paragraph",
      },
    ],
  });
}

function removeEmptyParagraphsAroundImages(doc: Node): Node {
  const result: Node[] = [];

  doc.descendants((node, pos) => {
    // Skip if we're inside a paragraph (we'll handle those at the paragraph level)
    if (node.type.name === "image" && doc.resolve(pos).parent.type.name !== "doc") {
      return false;
    }

    // Process top-level nodes
    if (doc.resolve(pos).parent.type.name === "doc") {
      if (node.type.name === "paragraph") {
        // Extract images and remaining content from paragraph
        const images: Node[] = [];
        const otherContent: Node[] = [];

        node.content.forEach((child) => {
          if (child.type.name === "image") {
            images.push(child);
          } else if (child.isText && child.text?.trim()) {
            otherContent.push(child);
          } else if (!child.isText) {
            otherContent.push(child);
          }
        });

        // Add images at top level
        images.forEach((img) => result.push(img));

        // Keep paragraph only if it has content
        if (otherContent.length > 0) {
          const newParagraph = node.type.create(node.attrs, Fragment.from(otherContent));
          result.push(newParagraph);
        }

        return false; // Don't descend into this node
      } else {
        // Keep other top-level nodes as-is
        result.push(node);
        return false;
      }
    }
  });

  return doc.type.create(doc.attrs, Fragment.from(result));
}
